// Detection of web addresses in message content.
//
// Sending links is forbidden in this system, in both directions. The reason is the
// one that made a phone number the company's address in the first place: a link is
// an object that can be swapped, and noticing the swap would take the user's
// attention — which the architecture assumes is never there.
//
// The client uses this twice, once per direction:
// * before sending — so it never sends something the server would reject anyway,
//   and can say why immediately instead of after a failed send;
// * when rendering — so incoming text never turns into a tappable link, even if the
//   company's server is hostile or merely careless.
//
// Ported pattern-for-pattern from the reference client's LinkGuard.

const PATTERN_SOURCE =
  '(?:' +
  // Scheme: https://…, http://…, ftp://…
  '\\b[a-z][a-z0-9+.-]{1,15}:\\/\\/\\S+' +
  '|' +
  // www.anything
  '\\bwww\\.[^\\s/]+\\.[a-z]{2,}\\S*' +
  '|' +
  // Bare domain with a common suffix, including one split by a space or written out.
  '\\b[a-z0-9][a-z0-9-]{0,62}\\s?(?:\\.|\\s*\\b(?:kropka|dot)\\b\\s*)\\s?' +
  '(?:pl|com|net|org|io|de|eu|uk|info|biz|shop|xyz|top|online|site|app|dev|co)\\b' +
  '(?:[/?#]\\S*)?' +
  ')';

// Undoes the tricks used to hide an address from naive matching: zero-width
// characters, extra spaces around dots, and dots spelled out as a word.
function normalise(text: string): string {
  return text
    .replace(/[​-‍﻿]/g, '')
    .replace(/\s*\.\s*/g, '.')
    .replace(/\s*\[\s*\.\s*\]\s*/g, '.')
    .replace(/\s*\(\s*\.\s*\)\s*/g, '.');
}

/** Whether the text contains something that is, or becomes, an address once joined up. */
export function containsLink(text: string): boolean {
  return new RegExp(PATTERN_SOURCE, 'i').test(normalise(text));
}

/** The fragments recognised as addresses, so the user can be told what is in the way. */
export function findLinks(text: string): string[] {
  return [...normalise(text).matchAll(new RegExp(PATTERN_SOURCE, 'gi'))].map((m) =>
    m[0].trim(),
  );
}

/**
 * A form safe to display: addresses stay visible as text but stop looking tappable.
 * Nothing is removed — the user is entitled to see what arrived.
 */
export function defang(text: string): string {
  return text.replace(new RegExp(PATTERN_SOURCE, 'gi'), (match) =>
    match.replaceAll('.', '[.]').replaceAll('://', '[://]'),
  );
}
